import { TabContext, TabPanel } from "@mui/lab";
import { TabLinks } from "components/TabLinks";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useSearchParams } from "react-router-dom";

import { ManifestDocuments } from "../ManifestDocuments/ManifestDocuments";
import { Sse } from "../Sse";
import { ManifestEmail } from "./ManifestEmail";
import { ScoutCleanupJobs } from "./ScoutCleanupJobs";
import { ScoutConversations } from "./ScoutConversations";
import { ScoutCostControl } from "./ScoutCostControl";
import { ScoutDashboard } from "./ScoutDashboard";
import { ScoutEvals } from "./ScoutEvals";
import { ScoutLoadTest } from "./ScoutLoadTest";
import { ScoutLocks } from "./ScoutLocks";
import { ScoutNodes } from "./ScoutNodes";

const DASHBOARD_TAB = "/staff/observe";
const COST_CONTROL_TAB = "/staff/observe?tab=cost-control";
const LOAD_TEST_TAB = "/staff/observe?tab=load-test";
const SCOUT_NODES_TAB = "/staff/observe?tab=scout-nodes";
const SCOUT_LOCKS_TAB = "/staff/observe?tab=scout-locks";
const SCOUT_CLEANUP_JOBS_TAB = "/staff/observe?tab=scout-cleanup-jobs";
const SCOUT_CONVERSATIONS_TAB = "/staff/observe?tab=scout-conversations";
const BENCHMARKS_TAB = "/staff/observe?tab=benchmarks";
const SSE_TAB = "/staff/observe?tab=sse";
const MANIFEST_DOCUMENTS_TAB = "/staff/observe?tab=manifest-documents";
// Seeded with the outcomes worth acting on, because the inbound grid's omnisearch is
// URL-driven: `initialFilters` applies only in local mode, and a mount effect that
// rewrote the URL would fight anyone arriving at a link of their own. Without this the
// tab opens on every outcome, so routine filed mail buries the actionable rows. Tab
// selection matches on this constant rather than the live URL, so the extra parameter
// does not disturb it — and changing the filter afterwards keeps the tab selected.
export const MANIFEST_EMAIL_TAB =
  "/staff/observe?tab=manifest-email&omnisearch=outcome%3ANEEDS_ATTENTION";

function getSelectedTabValue(tabParam: string | null): string {
  if (tabParam === "cost-control") {
    return COST_CONTROL_TAB;
  }
  if (tabParam === "load-test") {
    return LOAD_TEST_TAB;
  }
  if (tabParam === "scout-nodes") {
    return SCOUT_NODES_TAB;
  }
  if (tabParam === "scout-locks") {
    return SCOUT_LOCKS_TAB;
  }
  if (tabParam === "scout-cleanup-jobs") {
    return SCOUT_CLEANUP_JOBS_TAB;
  }
  if (tabParam === "scout-conversations") {
    return SCOUT_CONVERSATIONS_TAB;
  }
  if (tabParam === "benchmarks" || tabParam === "scout-evals") {
    return BENCHMARKS_TAB;
  }
  if (tabParam === "sse") {
    return SSE_TAB;
  }
  if (tabParam === "manifest-documents") {
    return MANIFEST_DOCUMENTS_TAB;
  }
  if (tabParam === "manifest-email") {
    return MANIFEST_EMAIL_TAB;
  }
  return DASHBOARD_TAB;
}

export function Observe() {
  useBreadcrumb({ label: "Observe" });

  const [searchParams] = useSearchParams();
  const { featureFlags } = useFeatureFlags();
  const selectedTabVal = getSelectedTabValue(searchParams.get("tab"));

  const tabs = [
    {
      title: "Dashboard",
      to: DASHBOARD_TAB,
      component: <ScoutDashboard />,
    },
    {
      title: "Cost Control",
      to: COST_CONTROL_TAB,
      component: <ScoutCostControl />,
    },
    {
      title: "Scout Nodes",
      to: SCOUT_NODES_TAB,
      component: <ScoutNodes />,
    },
    {
      title: "Scout Locks",
      to: SCOUT_LOCKS_TAB,
      component: <ScoutLocks />,
    },
    {
      title: "Scout Cleanup Jobs",
      to: SCOUT_CLEANUP_JOBS_TAB,
      component: <ScoutCleanupJobs />,
    },
    {
      title: "Scout Conversations",
      to: SCOUT_CONVERSATIONS_TAB,
      component: <ScoutConversations />,
    },
    {
      title: "Evals",
      to: BENCHMARKS_TAB,
      component: <ScoutEvals />,
    },
    {
      title: "Scout Load Test",
      to: LOAD_TEST_TAB,
      component: <ScoutLoadTest />,
    },
    ...(featureFlags[FeatureFlagKey.Sse]
      ? [
          {
            title: "SSE",
            to: SSE_TAB,
            component: <Sse />,
          },
        ]
      : []),
    // Not gated on the per-tenant ManifestDocumentIngest flag: this is a
    // cross-tenant staff observability view that runs with no current org, so
    // useFeatureFlags() would resolve that per-tenant flag to its default
    // (false) and the tab would never appear. The whole Observe section is
    // already staff-only, which is the correct gate here.
    {
      title: "Manifest Documents",
      to: MANIFEST_DOCUMENTS_TAB,
      component: <ManifestDocuments />,
    },
    // Staff-only like the rest of this section, and deliberately not gated on a
    // per-tenant flag: this runs with no current org, so useFeatureFlags() would
    // resolve any per-tenant flag to its default and hide the tab entirely.
    {
      title: "Manifest Email",
      to: MANIFEST_EMAIL_TAB,
      component: <ManifestEmail />,
    },
  ];

  return (
    <TabContext value={selectedTabVal}>
      <TabLinks tabs={tabs} value={selectedTabVal} scrollable aria-label="Observe tabs nav" />
      {tabs.map((tab) => (
        <TabPanel key={tab.to} value={tab.to} sx={{ px: 0 }}>
          {tab.component}
        </TabPanel>
      ))}
    </TabContext>
  );
}
